import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("Missing reset token. Use the link from your email.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      setErr("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — sign in now");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reset-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main className="max-w-md mx-auto px-4 pt-16 pb-24">
        <div className="text-center mb-10 anim-fade-up">
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D2926]">
            Set a new password
          </h1>
          <p className="mt-2 text-[#6B655D]">Take a breath. Then pick a fresh one.</p>
        </div>

        <div className="surface-card p-8 anim-fade-up anim-delay-1">
          {done ? (
            <p data-testid="reset-done" className="text-center text-[#2D2926]">
              Password updated. Redirecting…
            </p>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                  New password
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574] transition-all"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9A938A] mb-2">
                  Confirm
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  data-testid="reset-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-white border border-[#E6E2D8] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4A574] transition-all"
                  autoComplete="new-password"
                />
              </div>
              {err && (
                <p
                  data-testid="reset-error"
                  className="text-sm text-[#D98074] bg-[#FBEEEA] border border-[#F0CCC4] rounded-xl px-3 py-2"
                >
                  {err}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting || !token}
                data-testid="reset-submit-btn"
                className="w-full bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60"
              >
                {submitting ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
        </div>

        <p className="mt-8 text-center text-sm text-[#6B655D]">
          <Link
            to="/login"
            className="text-[#2D2926] hover:text-[#C29260] underline underline-offset-4"
          >
            Back to sign in
          </Link>
        </p>
      </main>
    </div>
  );
}
